// src/Components/PaywallModal.js
import React, { useState } from 'react';
import { useAuth } from '../Contexts/AuthContext';

export default function PaywallModal({ isOpen, onClose }) {
  const { user, isPremium, createCheckoutSession } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleUpgrade = async () => {
    if (!user) {
      alert('Please sign in first to upgrade');
      return;
    }

    try {
      setCheckoutLoading(true);
      await createCheckoutSession();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  const freeTierFeatures = [
    { text: 'Access to 5 days of SIA flight operations data', included: true },
    { text: 'Basic operational statistics (flights, routes, aircraft types)', included: true },
    { text: 'View individual SIA flight routes', included: true },
    { text: 'Basic route statistics for interview prep', included: true },
    { text: 'View flight distribution patterns', included: true },
    { text: 'Limited fleet information', included: true },
    { text: 'Filter by specific aircraft models', included: false },
    { text: 'Advanced route analytics (distance, frequency, operational patterns)', included: false },
    { text: 'Detailed flight-level operational data', included: false },
    { text: 'Access to 15 days of comprehensive flight data', included: false },
    { text: 'Complete aircraft model database access', included: false },
  ];

  const proTierFeatures = [
    { text: '15 days of comprehensive SIA operations data', included: true },
    { text: 'Complete operational statistics for interview preparation', included: true },
    { text: 'Filter by flight status and aircraft model (crucial for interviews)', included: true },
    { text: 'Deep dive into individual route operations', included: true },
    { text: 'Advanced route analytics (distance, frequency, day/night ops ratio)', included: true },
    { text: 'Detailed flight-level data to impress interviewers', included: true },
    { text: 'Access to all aircraft models deployed on each route', included: true },
    { text: 'Complete aircraft model database with technical specs', included: true },
    { text: 'Operational trends and fleet deployment patterns', included: true },
    { text: 'Priority data updates for latest operational insights', included: true },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[2100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-y-auto [scrollbar-width:none]"
          onClick={(e) => e.stopPropagation()}
          style={{
            maxHeight: 'calc(100dvh - 2rem)',
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: '0 8px 48px rgba(0,0,0,0.16)',
          }}
        >
          {/* Header */}
          <div className="relative pb-5 pt-6 px-6 border-b border-[#f0f0f0] text-center">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="#6b7280" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Plane icon */}
            <div className="flex justify-center mb-1">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#1d4ed8">
                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
              </svg>
            </div>

            {/* Badge */}
            <div className="flex justify-center mb-3">
              <span className="inline-flex items-center gap-1.5 bg-[#eff6ff] text-[#1d4ed8] text-xs font-semibold px-3 py-1 rounded-full border border-[#bfdbfe]">
                ✈ FOR SIA CADET APPLICANTS
              </span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold mt-3 text-center tracking-tight" style={{ color: '#111111' }}>
              Unlock Premium Access
            </h2>
            <p className="text-sm text-center mt-1" style={{ color: '#6b7280' }}>
              Get the operational insights that set you apart
            </p>

            {isPremium && (
              <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-3 text-center text-sm text-green-700 mt-4">
                You are already a Premium member — enjoy full access!
              </div>
            )}
          </div>

          {/* Value props strip */}
          <div className="bg-[#fafafa] border-b border-[#f0f0f0] px-6 py-5">
            <p className="text-sm font-semibold text-center mb-4" style={{ color: '#111111' }}>
              Why this matters for your interview
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl p-4 border border-[#f0f0f0]">
                <div className="text-xl mb-1">📊</div>
                <p className="text-sm font-semibold mb-1" style={{ color: '#111111' }}>Demonstrate Industry Knowledge</p>
                <p className="text-xs" style={{ color: '#6b7280' }}>Show deep understanding of SIA's fleet, routes, and operations</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#f0f0f0]">
                <div className="text-xl mb-1">🎯</div>
                <p className="text-sm font-semibold mb-1" style={{ color: '#111111' }}>Answer With Confidence</p>
                <p className="text-xs" style={{ color: '#6b7280' }}>Back your responses with real operational data and insights</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-[#f0f0f0]">
                <div className="text-xl mb-1">🚀</div>
                <p className="text-sm font-semibold mb-1" style={{ color: '#111111' }}>Outshine Other Candidates</p>
                <p className="text-xs" style={{ color: '#6b7280' }}>Access information most applicants don't know exists</p>
              </div>
            </div>
          </div>

          {/* Pricing cards */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">

              {/* Free Tier */}
              <div className="border border-[#e5e7eb] rounded-2xl p-5 sm:p-6">
                <h3 className="text-xl font-bold mb-1" style={{ color: '#111111' }}>Basic Access</h3>
                <div className="mb-1">
                  <span className="text-3xl font-bold" style={{ color: '#111111' }}>$0</span>
                  <span className="text-sm ml-1" style={{ color: '#6b7280' }}>/month</span>
                </div>
                <p className="text-xs mb-4" style={{ color: '#6b7280' }}>Perfect for interview prep and casual enthusiasts</p>

                <ul className="space-y-2 mb-6">
                  {freeTierFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      {feature.included ? (
                        <svg className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" style={{ color: '#d1d5db' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className={`text-xs ${feature.included ? '' : 'line-through'}`} style={{ color: feature.included ? '#374151' : '#9ca3af' }}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  disabled
                  className="border border-[#e5e7eb] text-[#9ca3af] rounded-xl py-3 font-medium w-full text-sm cursor-not-allowed"
                >
                  Current Plan
                </button>
              </div>

              {/* Pro Tier */}
              <div className="relative border-2 border-[#1d4ed8] rounded-2xl p-5 sm:p-6 bg-[#fefefe]">
                <span className="absolute top-0 right-4 -translate-y-1/2 bg-[#1d4ed8] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                  CADET RECOMMENDED
                </span>

                <h3 className="text-xl font-bold mt-2 mb-1" style={{ color: '#111111' }}>Interview Prep Pro</h3>
                <div className="mb-2">
                  <span className="text-xl font-bold line-through mr-1" style={{ color: '#d1d5db' }}>$15.99</span>
                  <span className="text-3xl font-bold" style={{ color: '#1d4ed8' }}>$7.99</span>
                  <span className="text-sm ml-1" style={{ color: '#6b7280' }}>/month</span>
                </div>
                <div className="mb-2">
                  <span className="inline-block bg-green-50 text-green-700 border border-green-200 rounded-full text-xs font-medium px-2.5 py-1">
                    Less than 2 cups of coffee
                  </span>
                </div>
                <p className="text-xs font-semibold mb-4" style={{ color: '#111111' }}>For serious cadet applicants & aviation enthusiasts</p>

                <ul className="space-y-2 mb-6">
                  {proTierFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" style={{ color: '#1d4ed8' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-xs font-medium" style={{ color: '#374151' }}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleUpgrade}
                  disabled={isPremium || checkoutLoading || !user}
                  className={`w-full py-3 rounded-xl font-semibold text-white transition-all text-sm ${
                    isPremium
                      ? 'bg-green-500 cursor-not-allowed'
                      : !user
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'hover:bg-[#c41230]'
                  }`}
                  style={
                    !isPremium && user
                      ? { background: '#1d4ed8' }
                      : undefined
                  }
                >
                  {checkoutLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : isPremium ? (
                    '✓ Active Subscription'
                  ) : !user ? (
                    'Sign In to Get Interview Advantage'
                  ) : (
                    'Get Interview-Ready Now'
                  )}
                </button>

                {!isPremium && user && (
                  <p className="text-center text-xs mt-2" style={{ color: '#6b7280' }}>
                    Cancel anytime · No commitment · Instant access
                  </p>
                )}

                {!isPremium && user && (
                  <p className="text-center text-xs mt-2" style={{ color: '#9ca3af' }}>
                    By subscribing, you agree to our{' '}
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent('openLegal', { detail: 'terms' }))}
                      className="underline hover:text-gray-600"
                    >
                      Terms
                    </button>
                    {' '}and{' '}
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent('openLegal', { detail: 'privacy' }))}
                      className="underline hover:text-gray-600"
                    >
                      Privacy Policy
                    </button>
                  </p>
                )}
              </div>
            </div>

            {/* ROI section */}
            <div className="mt-6 bg-[#111111] rounded-2xl p-6 text-white text-center">
              <p className="text-base font-semibold mb-5">Your Investment vs. The Career Potential</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <div className="text-3xl sm:text-4xl font-bold mb-1">$7.99</div>
                  <p className="text-sm" style={{ color: '#9ca3af' }}>Monthly investment in your aviation career</p>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold mb-1">vs</div>
                  <p className="text-sm" style={{ color: '#9ca3af' }}>One-time opportunity to impress SIA</p>
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-bold mb-1">$250K+</div>
                  <p className="text-sm" style={{ color: '#9ca3af' }}>Annual salary as SIA Captain</p>
                </div>
              </div>
              <p className="text-sm font-semibold mt-4" style={{ color: '#f59e0b' }}>
                Can you afford NOT to have every advantage?
              </p>
            </div>

            {/* Trust badges */}
            <div className="mt-6 pt-6 border-t border-[#f0f0f0] flex flex-wrap justify-center gap-4 sm:gap-6 text-xs" style={{ color: '#6b7280' }}>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Money-back Guarantee</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Instant Access</span>
              </div>
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Used by Successful Cadets</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
